import { ThreadVisibility } from "libs/schema";
import { createContext } from "react";
import { createUseContextHook } from "~/utils/createUseContextHook";
import { css, cx } from "@emotion/css";
import { useEffect } from "react";
import { List } from "~/components/list";
import { MentionableGroup } from "~/observables/observeMentionableGroups";
import { MentionableUser } from "~/observables/observeMentionableUsers";
import { ParentComponent } from "~/utils/type-helpers";
import { onSuggestionDialogClose } from "~/components/forms/tiptap/suggestion-utils";
import { onSuggestionDialogOpen } from "~/components/forms/tiptap/suggestion-utils";
import * as d from "ts-decoders/decoders";
import { isDecoderSuccess } from "ts-decoders";
import { Editor } from "@tiptap/core";

/* -----------------------------------------------------------------------------------------------*/

export const MentionExtensionId = `mention`;
export const DueDateExtensionId = `mention-due-date`;

/* -----------------------------------------------------------------------------------------------*/

export type MentionSuggestion = MentionableUser | MentionableGroup;

export interface IMentionAttributes {
  id: string;
  label: string;
  /** e.g. "user" or "group" */
  subject: string | null;
  priority: number;
  dueDate?: string;
  requestComplete?: boolean;
}

// It's been seen that, sometimes, the mention attributes returned by tiptap
// are unexpectedly stringified. This decoder attempts to handle those cases.
const mentionAttributesD = d.objectD<IMentionAttributes>({
  id: d.stringD(),
  label: d.stringD(),
  subject: d.nullableD(d.stringD().map((s) => s || null)),
  priority: d.anyOfD([
    d.integerD(),
    d.stringD().map((s) => {
      const priority = parseInt(s);
      if (isNaN(priority)) return 300;
      return priority;
    }),
  ]),
  dueDate: d.optionalD(d.stringD()).map((s) => s || undefined),
  requestComplete: d
    .optionalD(d.anyOfD([d.booleanD(), d.stringD().map((s) => s === "true")]))
    .map((v) => v ?? undefined),
});

export function decodeMentionAttributes(input: unknown) {
  const result = mentionAttributesD.decode(input);

  if (isDecoderSuccess(result)) {
    return result.value;
  }

  return null;
}

/* -----------------------------------------------------------------------------------------------*/

export interface IEditorMentionContext {
  restrictToVisibility: ThreadVisibility | null;
}

export const EditorMentionContext = createContext<IEditorMentionContext | null>(null);

export const useEditorMentionContext = createUseContextHook(EditorMentionContext, "EditorMentionContext");

/* -----------------------------------------------------------------------------------------------*/

export const SectionWrapper: ParentComponent<{}> = (props) => {
  return <div className="flex flex-col">{props.children}</div>;
};

export const SectionHeader: ParentComponent<{
  label: string;
}> = (props) => {
  return <h4 className="text-xs uppercase text-slate-9 font-medium mx-4 my-2">{props.label}</h4>;
};

export const Option: ParentComponent<{
  item: { id: string };
  relativeOrder: number;
  isDisabled?: boolean;
}> = (props) => {
  return (
    <List.Entry id={props.item.id} data={props.item} disabled={props.isDisabled} relativeOrder={props.relativeOrder}>
      <button
        type="button"
        className={cx(
          "flex items-center text-left mx-1 pr-3 py-1 rounded",
          "relative text-sm",
          optionCss,
          props.isDisabled && "text-slate-9",
        )}
      >
        {props.children}
      </button>
    </List.Entry>
  );
};

const optionCss = css`
  background-color: transparent;

  &.ListEntryActive {
    background-color: var(--slate5);
  }
`;

/* -----------------------------------------------------------------------------------------------*/

/** Should be called when a suggestion dropdown is opened. */
export function useOnSuggestionDropdownOpen() {
  useEffect(() => {
    // We need to track whether a suggestion dropdown is opened or not
    // for the EditorOverflowHandler. See `EditorOverflowHandler.ts`
    // for more info.
    onSuggestionDialogOpen();

    return onSuggestionDialogClose;
  }, []);
}

/* -----------------------------------------------------------------------------------------------*/

export function getMentionPrefix(query: string) {
  const match = query.match(/^(@+)/);

  const length = match?.[1]?.length || 0;

  // We need to account for the fact that the mention "char" isn't included
  // in the query string
  return new Array(length + 1).fill("@").join("");
}

export function getMentionPriority(query: string) {
  const prefix = getMentionPrefix(query);

  return (
    prefix.length === 1 ? 300
    : prefix.length === 2 ? 200
    : 100
  );
}

export function getMentionQueryWithoutPrefix(query: string) {
  return query.replace(/^@+/, "");
}

export interface IMentionExtensionStorage {
  threadId: string | null;
  mentionWeightAdjustments: Record<string, number>;
}

export function getMentionExtensionStorage(editor: Editor): IMentionExtensionStorage {
  const storage = editor.storage[MentionExtensionId] as IMentionExtensionStorage | undefined;

  if (!storage) {
    throw new Error("[getMentionExtensionStorage] storage not found");
  }

  return storage;
}

/* -----------------------------------------------------------------------------------------------*/
